/**
 * DPI (print resolution) metadata helpers.
 *
 * The image editor redraws every image on a <canvas> before exporting it, and
 * canvas encoders drop the density metadata: PNG output has no `pHYs` chunk and
 * JPEG output carries a JFIF header with `units = 0`. So a render produced at
 * 300 or 600 DPI comes back as 72 DPI once it has been through the editor.
 *
 * These helpers read the DPI off the source file and stamp it back onto the
 * encoded blob. Pixel dimensions are untouched - only the metadata changes.
 */

const INCH_IN_METRE = 39.3700787;
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47];

const isPng = (bytes) => PNG_SIGNATURE.every((byte, i) => bytes[i] === byte);
const isJpeg = (bytes) => bytes[0] === 0xff && bytes[1] === 0xd8;

let crcTable;

function crc32(bytes) {
  if (!crcTable) {
    crcTable = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      crcTable[n] = c >>> 0;
    }
  }

  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    crc = crcTable[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

/**
 * Walks the PNG chunk list looking for `type`. Metadata chunks always sit
 * before the first IDAT, so we stop there instead of scanning the pixel data.
 */
function findPngChunk(bytes, type) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let offset = 8; // skip the 8 byte signature

  while (offset + 8 <= bytes.length) {
    const length = view.getUint32(offset);
    const name = String.fromCharCode(bytes[offset + 4], bytes[offset + 5], bytes[offset + 6], bytes[offset + 7]);

    if (name === type) return offset;
    if (name === 'IDAT') return -1;

    offset += 12 + length; // 4 length + 4 type + data + 4 crc
  }
  return -1;
}

const hasAscii = (bytes, offset, text) => [...text].every((char, i) => bytes[offset + i] === char.charCodeAt(0));

/**
 * Finds a JPEG header segment by marker and identifier - APP0 'JFIF' holds the
 * density fields, APP1 'Exif' holds the resolution tags. Returns the offset of
 * the segment's 0xFF marker byte, or -1 when there isn't one.
 */
function findJpegSegment(bytes, marker, identifier) {
  let offset = 2; // skip SOI

  while (offset + 4 <= bytes.length && bytes[offset] === 0xff) {
    const current = bytes[offset + 1];

    if (current === 0xd8 || current === 0x01 || (current >= 0xd0 && current <= 0xd7)) {
      offset += 2;
      continue;
    }
    if (current === 0xda) break; // start of scan, no headers past this point

    if (current === marker && hasAscii(bytes, offset + 4, identifier)) return offset;

    offset += 2 + ((bytes[offset + 2] << 8) | bytes[offset + 3]);
  }
  return -1;
}

const findJfifApp0 = (bytes) => findJpegSegment(bytes, 0xe0, 'JFIF');
const findExifApp1 = (bytes) => findJpegSegment(bytes, 0xe1, 'Exif');

/**
 * Locates the EXIF resolution tags. Image viewers read these in preference to
 * the JFIF header, so they have to be kept in sync with it.
 */
function getExifResolution(bytes) {
  const app1 = findExifApp1(bytes);
  if (app1 === -1) return null;

  const tiff = app1 + 10; // skip marker, length and the 'Exif\0\0' identifier
  if (tiff + 8 > bytes.length) return null;

  const littleEndian = bytes[tiff] === 0x49;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const ifd0 = tiff + view.getUint32(tiff + 4, littleEndian);
  if (ifd0 + 2 > bytes.length) return null;

  const result = { littleEndian, view };

  const entryCount = view.getUint16(ifd0, littleEndian);
  for (let i = 0; i < entryCount; i++) {
    const entry = ifd0 + 2 + i * 12;
    if (entry + 12 > bytes.length) break;

    const tag = view.getUint16(entry, littleEndian);
    // XResolution and YResolution are RATIONALs, so the entry holds an offset.
    if (tag === 0x011a) result.xResolutionAt = tiff + view.getUint32(entry + 8, littleEndian);
    else if (tag === 0x011b) result.yResolutionAt = tiff + view.getUint32(entry + 8, littleEndian);
    else if (tag === 0x0128) result.unitAt = entry + 8; // SHORT, stored inline
  }

  return result.xResolutionAt !== undefined ? result : null;
}

function writePngDpi(bytes, dpi) {
  const ppm = Math.round(dpi * INCH_IN_METRE);

  // pHYs chunk: 4 length + 4 type + 9 data + 4 crc = 21 bytes
  const chunk = new Uint8Array(21);
  const chunkView = new DataView(chunk.buffer);
  chunkView.setUint32(0, 9);
  chunk.set([0x70, 0x48, 0x59, 0x73], 4); // 'pHYs'
  chunkView.setUint32(8, ppm);
  chunkView.setUint32(12, ppm);
  chunk[16] = 1; // unit specifier: metre
  chunkView.setUint32(17, crc32(chunk.subarray(4, 17)));

  const existing = findPngChunk(bytes, 'pHYs');
  if (existing !== -1) {
    const out = new Uint8Array(bytes);
    out.set(chunk, existing);
    return out;
  }

  // No pHYs yet - insert one straight after IHDR (signature 8 + chunk 25).
  const insertAt = 33;
  const out = new Uint8Array(bytes.length + chunk.length);
  out.set(bytes.subarray(0, insertAt), 0);
  out.set(chunk, insertAt);
  out.set(bytes.subarray(insertAt), insertAt + chunk.length);
  return out;
}

function writeJpegDpi(bytes, dpi) {
  const density = Math.min(65535, Math.round(dpi));
  const hi = density >> 8;
  const lo = density & 0xff;
  const app0 = findJfifApp0(bytes);
  let out;

  if (app0 !== -1) {
    out = new Uint8Array(bytes);
    out[app0 + 11] = 1; // units: dots per inch
    out[app0 + 12] = hi;
    out[app0 + 13] = lo;
    out[app0 + 14] = hi;
    out[app0 + 15] = lo;
  } else {
    // Canvas output normally has a JFIF header, but insert one if it doesn't.
    const segment = new Uint8Array([
      0xff, 0xe0, 0x00, 0x10, 0x4a, 0x46, 0x49, 0x46, 0x00, 0x01, 0x01, 0x01, hi, lo, hi, lo, 0x00, 0x00,
    ]);
    out = new Uint8Array(bytes.length + segment.length);
    out.set(bytes.subarray(0, 2), 0);
    out.set(segment, 2);
    out.set(bytes.subarray(2), 2 + segment.length);
  }

  // Keep EXIF in sync, otherwise viewers that prefer it would report the old DPI.
  const exif = getExifResolution(out);
  if (exif) {
    const { view, littleEndian, xResolutionAt, yResolutionAt, unitAt } = exif;
    [xResolutionAt, yResolutionAt].forEach((at) => {
      if (at === undefined || at + 8 > out.length) return;
      view.setUint32(at, density, littleEndian); // numerator
      view.setUint32(at + 4, 1, littleEndian); // denominator
    });
    if (unitAt !== undefined) view.setUint16(unitAt, 2, littleEndian); // unit: inches
  }

  return out;
}

/**
 * Reads the DPI out of raw PNG/JPEG bytes. Returns null when the file carries
 * no usable density metadata.
 */
export function readDpiFromBytes(bytes) {
  if (!bytes?.length) return null;

  if (isPng(bytes)) {
    const chunk = findPngChunk(bytes, 'pHYs');
    if (chunk === -1 || bytes[chunk + 16] !== 1) return null;

    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const ppm = view.getUint32(chunk + 8);
    return ppm ? Math.round(ppm / INCH_IN_METRE) : null;
  }

  if (isJpeg(bytes)) {
    // EXIF first - that is what image viewers read when both are present.
    const exif = getExifResolution(bytes);
    if (exif?.xResolutionAt !== undefined && exif.xResolutionAt + 8 <= bytes.length) {
      const { view, littleEndian, xResolutionAt, unitAt } = exif;
      const numerator = view.getUint32(xResolutionAt, littleEndian);
      const denominator = view.getUint32(xResolutionAt + 4, littleEndian) || 1;
      const unit = unitAt !== undefined ? view.getUint16(unitAt, littleEndian) : 2;
      const resolution = numerator / denominator;

      if (resolution) return unit === 3 ? Math.round(resolution * 2.54) : Math.round(resolution);
    }

    const app0 = findJfifApp0(bytes);
    if (app0 === -1) return null;

    const units = bytes[app0 + 11];
    const density = (bytes[app0 + 12] << 8) | bytes[app0 + 13];
    if (!density) return null;

    if (units === 1) return density; // dots per inch
    if (units === 2) return Math.round(density * 2.54); // dots per cm
    return null; // units 0 means aspect ratio only, no real DPI
  }

  return null;
}

/** Reads the DPI of an image blob. */
export async function readBlobDpi(blob) {
  if (!blob) return null;
  try {
    return readDpiFromBytes(new Uint8Array(await blob.arrayBuffer()));
  } catch (error) {
    return null;
  }
}

/** Reads the DPI of an image URL (remote or blob URL). */
export async function getSourceDpi(url) {
  if (!url) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    return readDpiFromBytes(new Uint8Array(await response.arrayBuffer()));
  } catch (error) {
    return null;
  }
}

/**
 * Stamps `dpi` onto a PNG or JPEG blob. Returns the blob untouched when there
 * is no DPI to write or the format isn't supported.
 */
export async function writeImageDpi(blob, dpi) {
  if (!blob || !dpi || dpi <= 0) return blob;

  try {
    const bytes = new Uint8Array(await blob.arrayBuffer());

    if (isPng(bytes)) return new Blob([writePngDpi(bytes, dpi)], { type: 'image/png' });
    if (isJpeg(bytes)) return new Blob([writeJpegDpi(bytes, dpi)], { type: 'image/jpeg' });

    return blob;
  } catch (error) {
    console.error('Failed to write DPI metadata:', error);
    return blob;
  }
}
